import { AlertTriangle, Building2, Check, CircleHelp, History, PlugZap, Search } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';

import type { NsPreview } from '../../../types/documentCapture.types';

/**
 * The facts that inform an identification (86d3x48q7 T13, bundle §4.2 — with the deviation below).
 *
 * Two sources sit side by side here and they answer DIFFERENT questions:
 *
 * 1. **What DOP has on file** ({@link CorroborationFacts}) — a snapshot taken when the readers
 *    disagreed, from `ScannedDocuments`, DOP's own list of documents it has already filed.
 * 2. **What NetSuite says** ({@link NetSuiteFacts}) — a live lookup of the customer and date on the
 *    transaction that carries this number.
 *
 * ⚠️ DISPLAYED, NEVER GATED ON. Gating on an unvalidated signal is exactly how the refuted quorum
 * gate happened. These inform the person; they never decide for them.
 *
 * ⚠️ (1) is a snapshot, not a live lookup. Once the reviewer attaches the scan, "already attached"
 * would answer differently, and the panel would quietly rewrite its own evidence while they were
 * reading it. (2) is deliberately live — a customer name and a transaction date do not move when
 * the reviewer acts, so there is nothing to freeze.
 *
 * ## 86d3z2nzb — why the registry fact is NEUTRAL, and the deviation it records
 *
 * This component used to print a green tick for "In the registry" and a red cross reading
 * **"Not in the registry — no such document exists"**. That sentence was false on every genuinely
 * new document: `GetCorroborationAsync` queries DOP's own filed list and never asks NetSuite, so
 * "not in the registry" only ever meant *DOP has not filed this number before* — the normal state
 * of the paper a reviewer is holding. Worse, the polarity was anti-correlated with the right answer
 * on intake: a NEW document is absent by construction, so any candidate that WAS present had to be
 * some other, previously-filed paper, and the tick landed on it.
 *
 * So: neutral glyph, neutral tone, and wording scoped to DOP. Destructive/warning tone is reserved
 * for **"already attached to another scan"**, which genuinely is a warning.
 *
 * ⚠️ **DEVIATION from `docs/feature-handoffs/design_handoff_ai_verification/README.md:152-153`
 * (§4.2)**, which specifies `check`/success and `circle-x`/destructive for this pair, with the
 * strings "In the registry" / "Not in the registry — no such document exists". Owner-decided
 * 2026-08-07 on ticket 86d3z2nzb; the bundle is untracked (git-excluded repo-wide) and lives only
 * in the main checkout, so this comment is the record of record. Do not "restore" the bundle
 * wording without re-reading that ticket.
 */
interface CorroborationFactsProps {
  /** Whether DOP has filed a document with this number before. NOT a NetSuite fact. */
  exists: boolean;
  /** Label of the scan already holding this number, or null when it is free. */
  claimedByFile: string | null;
  /** Customer/vendor on the previously filed document — the tell in the cross-customer case. */
  entityName: string | null;
}

export function CorroborationFacts({ exists, claimedByFile, entityName }: CorroborationFactsProps) {
  return (
    <ul className="mt-2 space-y-1">
      {/* Neutral by decision — see the deviation note above. Never a tick, never a cross. */}
      <Fact
        icon={<History className="h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />}
        text={
          exists
            ? entityName
              ? `DOP has filed this number before — ${entityName}`
              : 'DOP has filed this number before'
            : 'DOP has not filed this number before — normal for a document it has not handled yet'
        }
      />
      {/*
        86d3z2nzb — the old build always showed "Not attached to any other scan". When DOP has never
        filed the number, nothing CAN be attached to it, so that line was guaranteed green and told
        the reviewer nothing. It now appears only where it could have come out the other way.
      */}
      {claimedByFile != null ? (
        <Fact
          icon={<AlertTriangle className="h-3 w-3 flex-shrink-0 text-warning" aria-hidden />}
          text={`Already attached to ${claimedByFile}`}
          emphasis
        />
      ) : exists ? (
        <Fact
          icon={<Check className="h-3 w-3 flex-shrink-0 text-success" aria-hidden />}
          text="Not attached to any other scan"
        />
      ) : null}
    </ul>
  );
}

function Fact({ icon, text, emphasis = false }: { icon: React.ReactNode; text: string; emphasis?: boolean }) {
  return (
    <li className="flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
      <span className="mt-0.5">{icon}</span>
      <span className={emphasis ? 'text-foreground' : undefined}>{text}</span>
    </li>
  );
}

// ---------------------------------------------------------------- what NetSuite says (86d3z2nzb)

/**
 * One candidate's answer from NetSuite, or the reason there isn't one.
 *
 * The three no-answer cases are separate members ON PURPOSE. `GET netsuite/preview/{n}` 404s for
 * an unregistered prefix AND for a prefix outside the reviewer's permission scope
 * (`DocumentQueryService.PreviewNetSuiteAsync` returns null for both, deliberately — a distinct
 * response would let the endpoint be used to discover that a treasury series exists). An outage
 * throws instead and surfaces as a 5xx or a network failure. So the client can separate
 * *"the lookup did not happen"* from *"the lookup could not run"*, but it cannot split the first
 * one further — and the copy says so rather than inventing a cause.
 */
export type NetSuiteFact =
  | { kind: 'loading' }
  | { kind: 'match'; entityName: string | null; tranDate: string | null; voided: boolean }
  | { kind: 'none' }
  | { kind: 'many'; count: number }
  /** 404 — the number's series is not one this reviewer can look up here. Cause is ambiguous. */
  | { kind: 'notLookedUp' }
  /** Anything else — NetSuite could not be reached, so no check ran at all. */
  | { kind: 'unreachable' };

/** Pure: preview payload → the fact the card renders. */
export function netSuiteFactFromPreview(preview: NsPreview): NetSuiteFact {
  if (preview.count > 1) return { kind: 'many', count: preview.count };
  const match = preview.matches[0];
  if (preview.count !== 1 || !match) return { kind: 'none' };
  return {
    kind: 'match',
    entityName: match.entityName,
    tranDate: match.tranDate,
    voided: match.voided,
  };
}

/**
 * Pure: a failed preview → the fact the card renders.
 *
 * A 404 is a definite answer about the LOOKUP ("not something DOP can check for you"), never about
 * the document. Everything else — 5xx, a network drop, a `success:false` envelope with no HTTP
 * status — means the check did not run.
 */
export function netSuiteFactFromError(error: unknown): NetSuiteFact {
  return httpStatusOf(error) === 404 ? { kind: 'notLookedUp' } : { kind: 'unreachable' };
}

/**
 * A 404 from the preview endpoint is an ANSWER, not an outage: `DocumentQueryService` returns null —
 * and the controller 404s — both for an unregistered prefix and for one outside the caller's scope.
 * Any surface that renders "couldn't reach NetSuite" for this is telling the reviewer to retry
 * something that can never work. Exported so the number field below the cards applies the same test
 * to the same endpoint, instead of contradicting these cards forty lines further down the screen.
 */
export function isPrefixNotHandled(error: unknown): boolean {
  return httpStatusOf(error) === 404;
}

function httpStatusOf(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * The live NetSuite line under a candidate number.
 *
 * ⚠️ NOTHING here may read as "this document does not exist" unless NetSuite itself was asked and
 * answered — that is the whole of 86d3z2nzb. `none` is allowed to be blunt because it IS a NetSuite
 * answer; `notLookedUp` and `unreachable` are not, and must stay visibly different from it and from
 * each other.
 *
 * Loading is a scoped Skeleton, never a spinner, and it paints before either request resolves.
 */
export function NetSuiteFacts({ fact }: { fact: NetSuiteFact }) {
  if (fact.kind === 'loading') {
    return (
      <div className="mt-2 space-y-1">
        <span className="sr-only">Checking NetSuite for this number</span>
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
    );
  }

  if (fact.kind === 'match') {
    return (
      <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
        <Building2 className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0">
          <span className="block text-foreground">In NetSuite: {fact.entityName ?? 'no customer on the record'}</span>
          <span className="block tabular-nums">{fact.tranDate ?? 'no date on the record'}</span>
          {fact.voided && <span className="block text-warning">That transaction is voided.</span>}
        </span>
      </div>
    );
  }

  const line: { icon: React.ReactNode; text: string; tone?: string } =
    fact.kind === 'none'
      ? {
          icon: <Search className="mt-0.5 h-3 w-3 flex-shrink-0 text-warning" aria-hidden />,
          text: 'NetSuite has no transaction with this number.',
          tone: 'text-warning',
        }
      : fact.kind === 'many'
        ? {
            icon: <Search className="mt-0.5 h-3 w-3 flex-shrink-0 text-warning" aria-hidden />,
            text: `${fact.count} NetSuite transactions share this number.`,
            tone: 'text-warning',
          }
        : fact.kind === 'notLookedUp'
          ? {
              icon: <CircleHelp className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />,
              text: 'DOP can’t look this number up — its series isn’t set up here, or it’s outside what you can see.',
            }
          : {
              icon: <PlugZap className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />,
              text: 'Couldn’t reach NetSuite, so this number wasn’t checked there.',
            };

  return (
    <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
      {line.icon}
      <span className={line.tone}>{line.text}</span>
    </div>
  );
}
